import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Wrench, Home, ArrowLeft, ArrowRight, Check } from "lucide-react";
import { useApi, errMsg, TERMS, specLabel } from "@/lib/api";
import { useMe } from "@/context/Me";
import { PhotoPicker, Tape } from "@/components/bits";

const Choice = ({ active, onClick, children, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`panel flex items-center gap-4 p-5 text-left transition-[border-color,transform] duration-200 hover:-translate-y-0.5 ${active ? "!border-[#FFC700] bg-[#FFC700]/10" : "hover:border-white/30"}`}>
    {children}
  </button>
);

export default function Onboarding() {
  const api = useApi();
  const nav = useNavigate();
  const { meta, refresh, picked } = useMe();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ country: picked || "", profile_type: "", full_name: "", doc: "", birth_date: "", phone: "", state: "", city: "", district: "", address: "", postal_code: "", specialties: [], bio: "", doc_photo: [], selfie: [] });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const T = TERMS[f.country || "BR"];
  const isPro = f.profile_type === "pro";
  const last = isPro ? 3 : 2;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/onboarding", { ...f, doc_photo: f.doc_photo[0] || null, selfie: f.selfie[0] || null });
      await refresh();
      toast.success(isPro ? "Cadastro enviado! Sua verificação será analisada." : "Bem-vindo ao Zelobra!");
      nav(isPro ? "/mural" : "/inicio", { replace: true });
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const canNext = [f.country, f.profile_type, f.full_name && f.doc && f.birth_date && f.phone && f.city && f.state && f.district && f.address && f.postal_code, f.specialties.length && f.doc_photo.length && f.selfie.length][step];

  return (
    <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
      <div className="eyebrow mb-2">Cadastro · passo {step + 1} de {last + 1}</div>
      <div className="mb-8 flex gap-1.5">{Array.from({ length: last + 1 }).map((_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-[#FFC700]" : "bg-white/10"}`} />)}</div>

      {step === 0 && (
        <div className="fade-up space-y-4">
          <h1 className="text-4xl font-black uppercase">Onde você está?</h1>
          {["BR", "PT"].map((c) => (
            <Choice key={c} testid={`country-${c}`} active={f.country === c} onClick={() => setF({ ...f, country: c })}>
              <span className="text-4xl">{TERMS[c].flag}</span>
              <div><div className="text-xl font-bold">{TERMS[c].name}</div><div className="text-sm text-slate-400">{c === "BR" ? "Valores em R$ · CPF" : "Valores em € · NIF"}</div></div>
            </Choice>
          ))}
        </div>
      )}
      {step === 1 && (
        <div className="fade-up space-y-4">
          <h1 className="text-4xl font-black uppercase">Como vai usar o Zelobra?</h1>
          <Choice testid="profile-client" active={f.profile_type === "client"} onClick={() => setF({ ...f, profile_type: "client" })}>
            <div className="rounded-xl bg-[#FFC700] p-3 text-[#111317]"><Home /></div>
            <div><div className="text-xl font-bold">Sou cliente</div><div className="text-sm text-slate-400">Preciso de um reparo ou pequena obra</div></div>
          </Choice>
          <Choice testid="profile-pro" active={f.profile_type === "pro"} onClick={() => setF({ ...f, profile_type: "pro" })}>
            <div className="rounded-xl bg-[#FF5722] p-3 text-white"><Wrench /></div>
            <div><div className="text-xl font-bold">Sou profissional</div><div className="text-sm text-slate-400">Quero receber chamados perto de mim</div></div>
          </Choice>
          <p className="text-xs text-slate-500">Uma conta tem um único perfil.</p>
        </div>
      )}
      {step === 2 && (
        <div className="fade-up grid gap-4 sm:grid-cols-2">
          <h1 className="text-4xl font-black uppercase sm:col-span-2">Seus dados</h1>
          {[["full_name", "Nome completo", "Maria da Silva", "text", 2], ["doc", T.doc, T.docPh, "text", 1], ["birth_date", "Data de nascimento", "", "date", 1],
            ["phone", `${T.phone} (${T.prefix})`, T.phonePh, "tel", 1], ["postal_code", T.postal, T.postalPh, "text", 1],
            ["state", T.state, f.country === "PT" ? "Lisboa" : "SP", "text", 1], ["city", T.city, f.country === "PT" ? "Lisboa" : "São Paulo", "text", 1],
            ["district", T.district, f.country === "PT" ? "Arroios" : "Pinheiros", "text", 1], ["address", T.address, "Rua, número, complemento", "text", 1]].map(([k, l, ph, type, span]) => (
            <label key={k} className={span === 2 ? "sm:col-span-2" : ""}>
              <span className="label">{l}</span>
              <input data-testid={`onb-${k}`} className="field" type={type} placeholder={ph} value={f[k]} onChange={set(k)} />
            </label>
          ))}
          <p className="text-xs text-slate-500 sm:col-span-2">{T.doc}, data de nascimento e endereço ficam visíveis só para você e para a equipe Zelobra. É preciso ter 18 anos ou mais.</p>
        </div>
      )}
      {step === 3 && (
        <div className="fade-up space-y-6">
          <h1 className="text-4xl font-black uppercase">Perfil profissional</h1>
          <div>
            <span className="label">Especialidades</span>
            <div className="flex flex-wrap gap-2">
              {(meta?.specialties || []).map((s) => {
                const on = f.specialties.includes(s);
                return <button key={s} type="button" data-testid={`spec-${s}`} onClick={() => setF({ ...f, specialties: on ? f.specialties.filter((x) => x !== s) : [...f.specialties, s] })}
                  className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${on ? "border-[#FFC700] bg-[#FFC700] text-[#111317]" : "border-white/15 text-slate-300 hover:border-white/40"}`}>{on && <Check size={14} className="mr-1 inline" />}{specLabel(s, f.country)}</button>;
              })}
            </div>
          </div>
          <label className="block"><span className="label">Sobre você (opcional)</span>
            <textarea data-testid="onb-bio" className="field min-h-[100px]" value={f.bio} onChange={set("bio")} placeholder="Anos de experiência, tipos de serviço, região que atende…" /></label>
          <div className="panel space-y-4 p-5">
            <Tape className="rounded" />
            <div className="font-bold">Verificação de identidade</div>
            <p className="text-sm text-slate-400">Seu perfil só aparece publicamente após a aprovação da equipe Zelobra (selo Verificado).</p>
            <div className="flex flex-wrap gap-8">
              <div><span className="label">Foto do documento</span><PhotoPicker testid="onb-doc-photo" kind="private" max={1} value={f.doc_photo} onChange={(v) => setF((o) => ({ ...o, doc_photo: v }))} label="Documento" /></div>
              <div><span className="label">Selfie</span><PhotoPicker testid="onb-selfie" kind="private" max={1} capture="user" value={f.selfie} onChange={(v) => setF((o) => ({ ...o, selfie: v }))} label="Selfie" /></div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-10 flex justify-between gap-3">
        <button type="button" data-testid="onb-back" className="btn-ghost" disabled={step === 0} onClick={() => setStep(step - 1)}><ArrowLeft size={18} />Voltar</button>
        {step < last ? (
          <button type="button" data-testid="onb-next" className="btn-hazard" disabled={!canNext} onClick={() => setStep(step + 1)}>Continuar<ArrowRight size={18} /></button>
        ) : (
          <button type="button" data-testid="onb-submit" className="btn-hazard" disabled={!canNext || busy} onClick={submit}>{busy ? "Enviando…" : "Concluir cadastro"}<Check size={18} /></button>
        )}
      </div>
    </div>
  );
}
